/**
 * Drop-in replacement for Ant Design's `message` / `App.useApp()` toast API, backed
 * by sonner (already mounted via <Toaster/> in main.tsx). Lets us delete every
 * `import { App } from 'antd'` + `const { message } = App.useApp()` in favour of a
 * global `message` — no context provider needed (sonner is global).
 */
import { toast } from './Toast';

type Content = unknown;
const str = (c: Content) => (typeof c === 'string' ? c : String(c ?? ''));
const opt = (duration?: number) => (typeof duration === 'number' ? { duration: duration * 1000 } : undefined);

export const message = {
  success: (content: Content, duration?: number) => toast.success(str(content), opt(duration)),
  error: (content: Content, duration?: number) => toast.error(str(content), opt(duration)),
  warning: (content: Content, duration?: number) => toast.warning(str(content), opt(duration)),
  info: (content: Content, duration?: number) => toast(str(content), opt(duration)),
  loading: (content: Content, duration?: number) => toast.loading(str(content), opt(duration)),
  open: ({ type, content, duration }: { type?: 'success' | 'error' | 'warning' | 'info' | 'loading'; content: Content; duration?: number }) =>
    (message[type ?? 'info'] as (c: Content, d?: number) => void)(content, duration),
  destroy: () => toast.dismiss(),
};

/** antd `App.useApp()` shim — returns the same `{ message, notification }` surface. */
export const useMessage = () => message;
export const useApp = () => ({ message, notification: message });
